import { createInterceptableAjaxProxy, createInterceptableFetchProxy, Pipeline } from "../../utils";
import {
  Ajax, Fetch,
  FetchBody, FetchMethod,
  InterceptorsMap, InterceptURL,
  AfterFetch, AfterFetchInterceptor,
  BeforeFetch, BeforeFetchInterceptor
} from "./fetch.types";

export type Interceptor<T> = T extends (...args: infer Args) => infer Return ? Return extends Promise<any> ? (...args: Args) => Return : (...args: Args) => Promise<Return> : never;

interface InterceptorsRecord {
  before: Pipeline<BeforeFetch>,
  after: Pipeline<AfterFetch>,
}

export class GlobalInterceptor {
  private interceptors: InterceptorsRecord = {
    before: new Pipeline(),
    after: new Pipeline()
  }

  interceptFetchApi = (fetch: Fetch): Fetch => {
    return createInterceptableFetchProxy(fetch, {
      beforeFetch: this.onBeforeFetch,
      afterFetch: this.onAfterFetch
    });
  }

  interceptAjaxApi = (ajax: Ajax) => {
    createInterceptableAjaxProxy(ajax, {
      beforeFetch: this.onBeforeFetch,
      afterFetch: this.onAfterFetch
    });
  }

  intercept(url: InterceptURL, interceptor: InterceptorsMap) {
    const { before, after } = interceptor;
    if (before) this.interceptBefore(url, before);
    if (after) this.interceptAfter(url, after);
  }

  interceptBefore(interceptedURI: InterceptURL, interceptor: BeforeFetchInterceptor) {
    this.interceptors.before.push(async (url, method, body) => {
      if (GlobalInterceptor.shouldIntercept(interceptedURI, method, url)) {
        interceptor(body);
      }
    });
  }

  interceptAfter(interceptedURI: InterceptURL, interceptor: AfterFetchInterceptor) {
    this.interceptors.after.push(async (url, method, body, responseClone) => {
      if (GlobalInterceptor.shouldIntercept(interceptedURI, method, url)) {
        interceptor(body, responseClone);
      }
    });
  }

  private onBeforeFetch = async (url: string, method: FetchMethod, body: FetchBody): Promise<void> => {
    this.interceptors.before.execute(url, method, body);
  }

  private onAfterFetch = async (url: string, method: FetchMethod, body: FetchBody, responseClone: Response): Promise<void> => {
    this.interceptors.after.execute(url, method, body, responseClone);
  }

  static shouldIntercept = (interceptURL: InterceptURL, method: FetchMethod, url: string): boolean => {
    const [interceptedMethod, interceptedURL] = interceptURL.split(':');
    if (!interceptedMethod || !interceptedURL) {
      throw new TypeError(`Invalid interceptor URL in '${interceptURL}'. The URL must be in the format METHOD:/url`);
    }
    return !!(method.toLowerCase() === interceptedMethod?.toLowerCase() && url.match(new RegExp(`${interceptedURL}(\\?[^\\/]*)?$`, 'i')));
  }

  get frontEndMethods() {
    return {
      interceptBefore: this.interceptBefore,
      interceptAfter: this.interceptAfter
    }
  }
}
